import React, { useState, useCallback } from 'react'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useParams, Navigate } from 'react-router-dom'
import ProposalForm from 'proposals/proposal/ProposalForm'
import { Formik, Form } from 'formik'
import { useFormDynamicValuesValidationSchema } from 'utils/useFormDynamicValuesValidationSchema'
import * as yup from 'yup'
import Button from '@mui/material/Button'
import SelectCompany, { CompanyType } from 'proposals/components/SelectCompany'
import InfoBox from 'proposals/components/InfoBox'
import { useAttachments } from 'proposals/attachments/useAttachments'
import { useProposeRequestForProposal } from 'proposals/requestForProposal/queries/useRequestForProposalMutations'
import AddAttachments from 'proposals/attachments/AddAttachments'
import AddedDocumentsList from 'proposals/attachments/AddedDocumentsList'
import AddedImagesList from 'proposals/attachments/AddedImagesList'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import { v4 as uuidv4 } from 'uuid'
import { useQuery } from '@tanstack/react-query'
import { getProposalToken } from 'proposals/api/procurementApiClient'
import { useTranslation } from 'react-i18next'
import ProposalSubmitDialog from 'proposals/proposal/ProposalSubmitDialog'
import { UnsavedChangesReporter } from 'proposals/proposal/useReportUnsavedChanges'

const PREFIX = 'RequestForProposalFormContainer'

const classes = {
  formArea: `${PREFIX}-formArea`,
  sendButton: `${PREFIX}-sendButton`,
  attachmentsContainer: `${PREFIX}-attachmentsContainer`,
  documentsContainer: `${PREFIX}-documentsContainer`,
  errorMessage: `${PREFIX}-errorMessage`
}

const Root = styled('section')(({ theme: { spacing, palette } }) => ({
  [`&.${classes.formArea}`]: {
    gridArea: 'form'
  },

  [`& .${classes.sendButton}`]: {
    color: palette.common.white,
    marginTop: spacing(2),
    marginBottom: spacing(1)
  },

  [`& .${classes.attachmentsContainer}`]: {
    marginTop: spacing(2)
  },

  [`& .${classes.documentsContainer}`]: {
    marginTop: spacing(2),
    marginLeft: spacing(2)
  },

  [`& .${classes.errorMessage}`]: {
    marginTop: spacing(1),
    maxWidth: '650px'
  }
}))

type ProposeFormContainerProps = {
  fields: Field[]
  hasPropose: boolean | undefined
  isClosed: boolean
  recipient: RequestForProposaCurrentViewingRecipientType | null
  recipientCompany: { name: string; businessId: string } | null
  rfpLang: string
  region: string,
  isPreview?: boolean
  onDirtyChange?: (isDirty: boolean) => void
}

type FormValues = {
  person: {
    firstName: string
    lastName: string
    email: string
    phone: string
  }
  dynamicValues: {
    [x: string]: string
  }
}

const proposalId = uuidv4()

const ProposeFormContainer: React.FC<ProposeFormContainerProps> = ({
  fields,
  recipient,
  hasPropose,
  isClosed,
  recipientCompany,
  rfpLang,
  region,
  isPreview,
  onDirtyChange
}) => {
  const { t } = useTranslation()
  const { token } = useParams<{ token: string }>()

  const [company, setCompany] = useState<CompanyType | null>(recipientCompany)

  const [isSubmitDialogOpen, setIsSubmitDialogOpen] = useState<boolean>(false)

  const {
    mutate: propose,
    isPending: isLoadingPropose,
    isSuccess: isProposeSuccess,
    isError: isProposeError
  } = useProposeRequestForProposal(token as string)

  const { isLoading: isLoadingProposalContextToken, data: proposalContextToken = '' } = useQuery({
    queryKey: ['document-service-token'],
    queryFn: () => getProposalToken(token as string, proposalId),
    refetchOnWindowFocus: false
  })

  const inputFields = fields.filter((item) => item.type !== 'title')

  const {
    models: {
      images,
      imagesUploading,
      documents,
      documentsUploading,
      showUploadError,
      isImagesLoading,
      isDocumentsLoading
    },
    operations: { addDropzoneFiles, deleteImage, deleteDocument }
  } = useAttachments(proposalContextToken as string)

  const isUploadingFiles = imagesUploading.length > 0 || documentsUploading.length > 0

  const dynamicFieldValues = inputFields.reduce((memo, item) => {
    memo[item.id] = ''
    return memo
  }, {} as Record<string, string>)

  const { validationSchema } = useFormDynamicValuesValidationSchema(inputFields)

  const schema = yup.object().shape({
    dynamicValues: validationSchema,
    person: yup.object().shape({
      email: yup.string().email(t('invalid-email')).required(t('email-is-required'))
    })
  })

  const onSubmit = (values: FormValues): void => {
    const dynamicValues = Object.keys(values.dynamicValues).map((key) => ({
      fieldId: key,
      value: values.dynamicValues[key].toString()
    }))

    if (company === null || isUploadingFiles) {
      return
    }

    const contactPerson = {
      ...values.person,
      businessId: company.businessId,
      companyName: company.name,
      email: values.person.email.toLowerCase()
    }

    const request = {
      saveAsDraft: false,
      createWithId: proposalId,
      values: dynamicValues,
      contactPerson: contactPerson
    }

    propose(request)
  }

  const onSubmitDialogToggle = () => {
    setIsSubmitDialogOpen((prev) => !prev)
  }

  const selectCompany = useCallback(
    (value: CompanyType | null): void => {
      setCompany(value)
    },
    [setCompany]
  )

  if (isProposeSuccess) {
    return <Navigate to={`/${token}/success`} replace />
  }

  if (isProposeError) {
    return <Navigate to="/error" replace />
  }

  return (
    <Root className={classes.formArea}>
      {isClosed && <InfoBox text={t('request-for-proposal-is-closed')} />}

      {!hasPropose && !isClosed && (
        <>
          <Typography color="primary" variant="h6" gutterBottom>
            {isPreview ? t('preview-proposal-form') : t('submit-proposal')}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {t('vendor-information')}
          </Typography>
          <SelectCompany selectCompany={selectCompany} selectedValue={company} region={region} disabled={isPreview} />
          <Formik
            validationSchema={schema}
            initialValues={{
              person: {
                firstName: '',
                lastName: '',
                email: recipient?.currentViewingRecipientEmail.trim() ?? '',
                phone: ''
              },
              dynamicValues: {
                ...dynamicFieldValues
              }
            }}
            onSubmit={onSubmit}
          >
            {({ isValid, dirty, submitForm }): React.ReactNode => (
              <Form>
                <UnsavedChangesReporter
                  formikDirty={dirty}
                  companyBusinessId={company?.businessId}
                  initialBusinessId={recipientCompany?.businessId}
                  imageIds={images.map((image) => image.id)}
                  documentIds={documents.map((document) => document.id)}
                  attachmentsReady={!!proposalContextToken && !isImagesLoading && !isDocumentsLoading}
                  isUploadingFiles={isUploadingFiles}
                  onDirtyChange={onDirtyChange}
                />
                <ProposalForm fields={fields} rfpLang={rfpLang} isPreview={isPreview} />
                <Box sx={{ mt: 2 }}>
                  <AddAttachments
                    disabled={!proposalContextToken || isLoadingProposalContextToken || isPreview}
                    addDropzoneFiles={addDropzoneFiles}
                  />
                  <Box sx={{ mt: 2, ml: 2 }}>
                    <AddedDocumentsList
                      documents={documents}
                      documentsUploading={documentsUploading}
                      deleteDocument={deleteDocument}
                    />
                    <AddedImagesList images={images} imagesUploading={imagesUploading} deleteImage={deleteImage} />
                    {showUploadError && (
                      <Alert sx={{ mt: 1, maxWidth: '650px' }} severity="error">
                        {t('attachment-error-text')}
                      </Alert>
                    )}
                  </Box>
                </Box>
                {!isPreview && (
                <Button
                  className={classes.sendButton}
                  variant="contained"
                  color="primary"
                  onClick={onSubmitDialogToggle}
                  disabled={!dirty || !isValid || company === null || isLoadingPropose || isUploadingFiles}
                >
                  {t('send-proposal')}
                </Button>)}
                <ProposalSubmitDialog
                  onClose={onSubmitDialogToggle}
                  open={isSubmitDialogOpen}
                  submitForm={submitForm}
                  isLoadingPropose={isLoadingPropose}
                  rfpLang={rfpLang}
                />
              </Form>
            )}
          </Formik>
        </>
      )}
    </Root>
  )
}

export default ProposeFormContainer
